import { Alert, TextField } from "@mui/material";
import * as React from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import {
  PageBreadcrumbs,
  PageLayout,
  RobotoButton,
  RobotoLoadingButton,
} from "@/shared/components";
import { FilePicker } from "@/shared/components/FilePicker";
import { Goto } from "@/shared/navigation/Goto";
import { type CreateSessionLocationState } from "@/shared/navigation/types";

import styles from "./CreateSessionPage.module.css";
import { DevicesSection } from "./DevicesSection";
import { FilesSection } from "./FilesSection";
import { useCreateSessionForm } from "./useCreateSessionForm";

/**
 * Full-page create-session form at `/sessions/create`. Reachable from the
 * top-level Sessions page, the Sessions tab in dataset details, and a
 * dataset's file-browser selection menu — dataset-scoped entry points seed
 * the file selection via the `dataset_id` query param and navigation state.
 */
export function CreateSessionPage() {
  const orgId = useCurrentOrgId();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  // Normalize both a missing param and the malformed empty value
  // (`?dataset_id=`) to undefined, so every downstream "dataset in scope"
  // guard agrees on one definition.
  const datasetId = searchParams.get("dataset_id") || undefined;

  const location = useLocation();
  const locationState = location.state as CreateSessionLocationState | null;

  const form = useCreateSessionForm({
    orgId,
    datasetId,
    fileIds: locationState?.fileIds,
    directoryPatterns: locationState?.directoryPatterns,
  });

  const [filePickerOpen, setFilePickerOpen] = React.useState(false);

  const handleCancel = () => {
    // On a direct entry (deep link, refresh, new tab) there is no in-app
    // history entry behind this page — `navigate(-1)` would no-op or leave
    // the SPA — so fall back to the Sessions list.
    if (location.key === "default") {
      navigate(Goto.route.Sessions);
    } else {
      navigate(-1);
    }
  };

  return (
    <PageLayout
      title="Roboto - Create session"
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Sessions}>Sessions</Link>
          <span>Create session</span>
        </PageBreadcrumbs>
      }
      footer={
        <div className={styles.footerActions}>
          <RobotoButton
            variant="text"
            size="large"
            eventName="CreateSessionCanceled"
            onClick={handleCancel}
          >
            Cancel
          </RobotoButton>
          <RobotoLoadingButton
            variant="contained"
            size="large"
            loading={form.isSubmitting}
            // Submitting mid-prefill — or after the prefill failed — would
            // create the session without the entry point's seeded files,
            // silently dropping the user's selection intent.
            disabled={form.isPrefilling || form.prefillError !== null}
            eventName="CreateSessionSubmitted"
            eventProperties={{
              datasetId,
              fileCount: form.selectedFiles.length,
            }}
            onClick={() => {
              void form.submit();
            }}
          >
            Create session
          </RobotoLoadingButton>
        </div>
      }
    >
      <div className={styles.container}>
        <DatasetSubhead datasetId={datasetId} />

        <section className={styles.section}>
          <TextField
            id="session-name"
            label="Name"
            value={form.name}
            fullWidth
            size="small"
            // Mirrors the API's 120-character cap on session names.
            inputProps={{ maxLength: 120 }}
            placeholder="Optional — e.g. Morning test drive"
            helperText="You can leave this blank and rename the session later."
            onChange={(event) => form.setName(event.target.value)}
          />
        </section>

        <FilesSection
          files={form.selectedFiles}
          isLoadingFiles={form.isPrefilling}
          filesLoadError={form.prefillError}
          onAddFiles={() => setFilePickerOpen(true)}
          onRemoveFiles={form.removeFiles}
        />

        <DevicesSection
          orgId={orgId}
          inferredDeviceIds={form.inferredDeviceIds}
          isInferringDevices={form.isInferringDevices}
          inferDevicesError={form.inferDevicesError}
          explicitDeviceIds={form.explicitDeviceIds}
          onAddDevice={form.addDevice}
          onRemoveDevice={form.removeDevice}
        />

        <SubmitErrorBanner message={form.submitError} />
      </div>

      <FilePicker
        orgId={orgId}
        open={filePickerOpen}
        onClose={() => setFilePickerOpen(false)}
        onSubmit={form.mergeFiles}
        title="Add files to session"
        activeDatasetIds={datasetId ? [datasetId] : undefined}
        existingFileIds={form.selectedFiles.map((file) => file.file_id)}
      />
    </PageLayout>
  );
}

function DatasetSubhead({ datasetId }: { datasetId: string | undefined }) {
  if (datasetId === undefined) {
    return null;
  }
  return (
    <p className={styles.muted}>
      Creating from dataset{" "}
      <Link to={Goto.createDatasetRoute(datasetId)}>{datasetId}</Link>
    </p>
  );
}

function SubmitErrorBanner({ message }: { message: React.ReactNode }) {
  // Cover every value React renders as nothing — not just `null` — so an
  // empty message can't paint an empty error box.
  if (
    message === null ||
    message === undefined ||
    message === false ||
    message === true ||
    message === ""
  ) {
    return null;
  }
  return <Alert severity="error">{message}</Alert>;
}
